import { Injectable } from '@angular/core';

import { Observable, ReplaySubject, Subject } from 'rxjs';
import { take } from 'rxjs/operators';

import { ClientConnection } from '../../models';

import { AuthenticationService } from '../authentication.service';
import { ClientConnectionsService } from '../client-connections.service';
import { UsersService } from '../users.service';

import { FirestoreService } from './firestore.service';

export interface ClientConnectionDTO {
    id: string;
    name: string;
    tagId: string;
}

@Injectable({
    providedIn: 'root',
})
export class FirestoreClientConnectionsService implements ClientConnectionsService {
    private _clientConnectionsSubject: Subject<ClientConnection[]>;

    private _clientConnections: ClientConnection[];

    constructor(
        private authenticationService: AuthenticationService,
        private firestoreService: FirestoreService,
        private usersService: UsersService
    ) {
        this._clientConnectionsSubject = new ReplaySubject<ClientConnection[]>(1);

        this.initFirestoreCollectionObservable();
    }

    clientConnections(): Observable<ClientConnection[]> {
        return this._clientConnectionsSubject.asObservable();
    }

    async clientConnection(id: string): Promise<ClientConnection> {
        const findClientConnection = (clientConnection: ClientConnection[]) => clientConnection.find((o) => o.id === id);

        return new Promise<ClientConnection>((resolve) => {
            if (this._clientConnections) {
                // client connections are already synced with firestore
                resolve(findClientConnection(this._clientConnections));
            } else {
                // client connections are not yet resolver, wait until the _clientConnectionsSubject has any data to resolve it
                this._clientConnectionsSubject.pipe(take(1)).subscribe((clientConnections) => resolve(findClientConnection(clientConnections)));
            }
        });
    }

    private initFirestoreCollectionObservable(): void {
        this.firestoreService.tenantDependentCollection<ClientConnectionDTO>(this.authenticationService.user.tenantId, 'clientConnections')
            .snapshotChanges()
            .subscribe((result) => {
                const clientConnectionsDTO = result.map((cc) => {
                    const clientConnection = cc.payload.doc.data();
                    clientConnection.id = cc.payload.doc.id;
                    return clientConnection;
                });

                const clientConnections = clientConnectionsDTO.map((clientConnection) => 
                    this.clientConnectionMapper(clientConnection)
                );

                this._clientConnections = clientConnections;
                this.updateClientConnections(clientConnections);
        });
    }

    private clientConnectionMapper(clientConnectionDTO: ClientConnectionDTO): ClientConnection {
        return {
            id: clientConnectionDTO.id,
            name: clientConnectionDTO.name,
            tagId: clientConnectionDTO.tagId
        };
    }

    private updateClientConnections(clientConnections: ClientConnection[]): void {
        this._clientConnectionsSubject.next(clientConnections);
    }
}
